import React from 'react';
import { observer } from 'mobx-react';
import { Box, Flex, Spinner, Text } from 'core/components';
import CoreSelect from 'core/components/select/Select';
import { Field, InputGroup } from 'core/form';
import { SynStore } from 'app/stores/synthetics/synthTypes';
import { FilterState } from '../filter.types';
import { useTestOptions, TestOptionData, getTestTypeLabel } from './useTestOptions';

interface TestFilterInputProps {
  filter: FilterState;
  operator: string;
  $syn?: SynStore;
  autoFocus?: boolean;
}

// Check if operator is an equality operator (= or <>)
function isEqualityOperator(operator: string): boolean {
  return operator === '=' || operator === '<>';
}

// Test option renderer for Select component
const TestOptionRenderer = ({ label, testType, target }: TestOptionData) => (
  <Box py="4px">
    <Text fontWeight="bold" ellipsis>
      {label}
    </Text>
    {testType && (
      <Text small muted ellipsis>
        {getTestTypeLabel(testType)}
        {target && ` • ${target}`}
      </Text>
    )}
  </Box>
);

// Test value renderer for Select component (shown when option is selected)
const TestValueRenderer = (option: TestOptionData | null) => {
  if (!option) {
    return <span>Select test...</span>;
  }
  const { label } = option;
  return (
    <Box py="2px">
      <Text fontWeight="bold" ellipsis>
        {label}
      </Text>
    </Box>
  );
};

/**
 * Filter value input component for test dimensions.
 * - Equality operators (=, <>) show a rich dropdown with test details
 * - Non-equality operators (ILIKE, regex) show a text input for searching by name
 */
const TestFilterInput = observer(({ filter, operator, $syn, autoFocus }: TestFilterInputProps) => {
  const showSelect = isEqualityOperator(operator);
  const { options, isLoading } = useTestOptions($syn, showSelect);

  // Equality operator - use select with rich rendering
  if (showSelect) {
    if (isLoading || !options) {
      return (
        <Flex alignItems="center" gap={1} p={1}>
          <Spinner size={14} />
          <Box color="muted">Loading tests...</Box>
        </Flex>
      );
    }

    const currentValue = filter.filterValue.getValue();
    const handleChange = (value: string) => {
      filter.filterValue.setValue(value);
    };

    return (
      <Box>
        <CoreSelect
          small
          fill
          showFilter
          autoFocus={autoFocus}
          placeholder="Select test..."
          options={options}
          values={currentValue}
          onChange={handleChange}
          optionFormatter={TestOptionRenderer}
          valueRenderer={TestValueRenderer}
        />
      </Box>
    );
  }

  // Non-equality operator (contains, regex) - use text input
  // The filter will be transformed to match test IDs when applied
  return (
    <Field field={filter.filterValue} showLabel={false} mb={0}>
      <InputGroup small fill autoFocus={autoFocus} placeholder="Search test name..." />
    </Field>
  );
});

export default TestFilterInput;
